import { createContext, useContext, useEffect } from 'react';

import PropTypes from 'prop-types';

import { useScopedSelection } from '#globalHooks/useScopedSelection';

import { useContactSelection } from '../hooks/useContactSelection';

import { useLeadData } from './LeadDataContext';

const LeadUIContext = createContext(null);

/**
 * Hook to access lead UI context.
 * Provides selection state for contacts and leads.
 * For UI state (modals, selectedContact etc.) use useLeadUIStore / usePreferencesStore directly.
 */
export const useLeadUI = () => {
	const context = useContext(LeadUIContext);
	if (!context) {
		throw new Error('useLeadUI must be used within LeadUIProvider');
	}
	return context;
};

/**
 * Provider for lead selection logic.
 * Only keeps state that depends on React hooks (useContactSelection, useScopedSelection).
 */
export const LeadUIProvider = ({ children }) => {
	const { contactsArray, leads, totalCount, selectedSearchQueryId, currentPage, orderBy, orderDir, filters } =
		useLeadData();

	const {
		selectedContacts,
		setSelectedContacts,
		handleContactToggle,
		handleSelectAllContacts,
		clearSelection: clearContactSelection,
		allContactsSelected,
	} = useContactSelection(contactsArray);

	// Filters are compared by value: parseLeadFilters emits keys in a fixed order,
	// so the JSON string is stable and avoids object-identity churn.
	const filtersKey = JSON.stringify(filters);

	const leadSelection = useScopedSelection(leads, {
		totalCount,
		filters,
		resetKey: `${selectedSearchQueryId}|${filtersKey}`,
		entityLabel: { singular: 'Firma', plural: 'Firmen', gender: 'f' },
	});
	const { tableSelection, clear: clearLeadSelection, hasSelection: hasLeadSelection } = leadSelection;

	// Selected contacts are only visible inside expanded rows of the current view.
	useEffect(() => {
		setSelectedContacts([]);
	}, [selectedSearchQueryId, currentPage, orderBy, orderDir, filtersKey, setSelectedContacts]);

	// Lead and contact selection are mutually exclusive: every consumer (bulk bars, CRM
	// transfer, contact lists, CSV export) acts on exactly one of the two, so a mixed
	// selection would be silently half-ignored. Selecting on one side clears the other.
	const withoutContacts = (handler) => (arg) => {
		if (selectedContacts.length > 0) setSelectedContacts([]);
		handler(arg);
	};

	const handleContactToggleExclusive = (contactId) => {
		if (hasLeadSelection) clearLeadSelection();
		handleContactToggle(contactId);
	};

	const handleSelectAllContactsExclusive = () => {
		if (hasLeadSelection) clearLeadSelection();
		handleSelectAllContacts();
	};

	const leadTableSelection = {
		...tableSelection,
		onToggleOne: withoutContacts(tableSelection.onToggleOne),
		onToggleAll: withoutContacts(tableSelection.onToggleAll),
		scopeBanner: {
			...tableSelection.scopeBanner,
			onSelectAllInFilter: withoutContacts(tableSelection.scopeBanner.onSelectAllInFilter),
		},
	};

	const contextValue = {
		// Contact selection
		selectedContacts,
		setSelectedContacts,
		allContactsSelected,
		handleContactToggle: handleContactToggleExclusive,
		handleSelectAllContacts: handleSelectAllContactsExclusive,
		clearContactSelection,
		hasContactSelection: selectedContacts.length > 0,

		// Lead selection
		selectedLeads: leadSelection.selectedIds,
		selectAllInFilter: leadSelection.selectAllInFilter,
		hasLeadSelection,
		leadSelectionCount: leadSelection.count,
		leadActionTarget: leadSelection.actionTarget,
		leadActionScopeLabel: leadSelection.actionScopeLabel,
		leadEntityLabel: leadSelection.entityLabel,
		leadTableSelection,
		clearLeadSelection,
	};

	return <LeadUIContext.Provider value={contextValue}>{children}</LeadUIContext.Provider>;
};

LeadUIProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
